import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Sparkles, Plus, FileText, Users, Layers, Trash2, Pencil, Send,
  Loader2, Search, Printer, ChevronRight, MessageCircle, Link2, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import {
  getDetailingClients, createDetailingClient, updateDetailingClient, deleteDetailingClient,
  getDetailingServices, createDetailingService, updateDetailingService, deleteDetailingService,
  getDetailingQuotes, createDetailingQuote, updateDetailingQuoteStatus, deleteDetailingQuote,
} from '../lib/api';

const STATUS_META = {
  borrador: { label: 'Borrador', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  aceptada: { label: 'Aceptada', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-700 border-red-200' },
  convertida: { label: 'Convertida', color: 'bg-violet-100 text-violet-700 border-violet-200' },
};

export default function AdminDetailingPage() {
  const [tab, setTab] = useState('quotes'); // quotes | catalog | clients
  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-violet-600" />
              Detailing
            </h1>
            <p className="text-slate-600 mt-1">Cotizaciones premium · catálogo libre · clientes propios</p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200" data-testid="detailing-tabs">
          <TabButton active={tab === 'quotes'} onClick={() => setTab('quotes')} icon={FileText} label="Cotizaciones" testId="tab-quotes" />
          <TabButton active={tab === 'catalog'} onClick={() => setTab('catalog')} icon={Layers} label="Catálogo" testId="tab-catalog" />
          <TabButton active={tab === 'clients'} onClick={() => setTab('clients')} icon={Users} label="Clientes" testId="tab-clients" />
        </div>

        {tab === 'quotes' && <QuotesTab />}
        {tab === 'catalog' && <CatalogTab />}
        {tab === 'clients' && <ClientsTab />}
      </div>
    </DashboardLayout>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// =================================================================
// CLIENTS TAB
// =================================================================
function ClientsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // null = create, object = edit
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getDetailingClients();
      setItems(r.data);
    } catch { toast.error('Error al cargar clientes'); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.vehicle_info || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar cliente?')) return;
    try {
      await deleteDetailingClient(id);
      toast.success('Cliente eliminado');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="card-base p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="new-det-client-btn">
          <Plus className="w-4 h-4" /> Nuevo cliente
        </Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : filtered.length === 0 ? (
        <p className="text-slate-400 text-center py-8">Sin clientes</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="table-header">Nombre</th>
                <th className="table-header">Teléfono</th>
                <th className="table-header">Vehículo</th>
                <th className="table-header"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="table-row" data-testid={`det-client-row-${c.id}`}>
                  <td className="table-cell font-medium">{c.name}</td>
                  <td className="table-cell">{c.phone || '—'}</td>
                  <td className="table-cell text-slate-600 truncate max-w-xs">{c.vehicle_info || '—'}</td>
                  <td className="table-cell text-right">
                    <button onClick={() => { setEdit(c); setOpen(true); }} className="text-blue-600 hover:text-blue-800 mr-3"><Pencil className="w-4 h-4 inline" /></button>
                    <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClientFormModal open={open} onOpenChange={setOpen} initial={edit} onSaved={refresh} />
    </div>
  );
}

function ClientFormModal({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', vehicle_info: '', notes: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      name: initial?.name || '', phone: initial?.phone || '', email: initial?.email || '',
      vehicle_info: initial?.vehicle_info || '', notes: initial?.notes || '',
    });
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setBusy(true);
    try {
      if (initial) await updateDetailingClient(initial.id, form);
      else await createDetailingClient(form);
      toast.success(initial ? 'Cliente actualizado' : 'Cliente creado');
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="det-client-form-modal">
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar cliente' : 'Nuevo cliente Detailing'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus data-testid="det-client-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="det-client-phone" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div><Label>Vehículo (marca/modelo/color/placas)</Label><Input value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} placeholder="Ej: Honda Civic 2022 rojo ABC-123" /></div>
          <div><Label>Notas</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-violet-600 hover:bg-violet-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// CATALOG TAB
// =================================================================
function CatalogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setItems((await getDetailingServices()).data); } catch { toast.error('Error'); }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar servicio del catálogo?')) return;
    try { await deleteDetailingService(id); toast.success('Eliminado'); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="card-base p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-600">El catálogo crece automáticamente cuando agregas servicios libres en una cotización.</p>
        <Button onClick={() => { setEdit(null); setOpen(true); }} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="new-det-service-btn">
          <Plus className="w-4 h-4" /> Nuevo servicio
        </Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : items.length === 0 ? (
        <p className="text-slate-400 text-center py-8">Sin servicios. El catálogo se irá llenando solo.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(s => (
            <div key={s.id} className="border border-slate-200 rounded-xl p-3 hover:border-violet-300 transition-colors" data-testid={`det-service-card-${s.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                {s.is_custom && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Aprendido</span>}
              </div>
              {s.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{s.description}</p>}
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-slate-600">{s.suggested_price ? `Sug: ${formatCurrency(s.suggested_price)}` : 'Precio libre'}</span>
                <div>
                  <button onClick={() => { setEdit(s); setOpen(true); }} className="text-blue-600 hover:text-blue-800 mr-2"><Pencil className="w-3.5 h-3.5 inline" /></button>
                  <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ServiceFormModal open={open} onOpenChange={setOpen} initial={edit} onSaved={refresh} />
    </div>
  );
}

function ServiceFormModal({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', suggested_price: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({
      name: initial?.name || '', description: initial?.description || '',
      suggested_price: initial?.suggested_price != null ? String(initial.suggested_price) : '',
    });
  }, [open, initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        suggested_price: form.suggested_price ? parseFloat(form.suggested_price) : null,
      };
      if (initial) await updateDetailingService(initial.id, payload);
      else await createDetailingService(payload);
      toast.success(initial ? 'Servicio actualizado' : 'Servicio creado');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="det-service-form-modal">
        <DialogHeader><DialogTitle>{initial ? 'Editar servicio' : 'Nuevo servicio'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus data-testid="det-service-name" /></div>
          <div><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label>Precio sugerido (opcional)</Label><Input type="number" step="0.01" value={form.suggested_price} onChange={(e) => setForm({ ...form, suggested_price: e.target.value })} placeholder="Se puede ajustar en cada cotización" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-violet-600 hover:bg-violet-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =================================================================
// QUOTES TAB
// =================================================================
function QuotesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [viewing, setViewing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setItems((await getDetailingQuotes()).data); } catch { toast.error('Error al cargar'); }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setOpenCreate(true)} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="new-quote-btn">
          <Plus className="w-4 h-4" /> Nueva cotización
        </Button>
      </div>

      <div className="card-base p-0 overflow-hidden">
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : items.length === 0 ? (
          <p className="text-slate-400 text-center py-10">Sin cotizaciones. Crea la primera.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="table-header">Folio</th>
                <th className="table-header">Cliente</th>
                <th className="table-header">Items</th>
                <th className="table-header text-right">Total</th>
                <th className="table-header">Estado</th>
                <th className="table-header">Fecha</th>
                <th className="table-header text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(q => (
                <tr key={q.id} className="table-row cursor-pointer" onClick={() => setViewing(q)} data-testid={`quote-row-${q.id}`}>
                  <td className="table-cell font-mono text-violet-600">{q.folio}</td>
                  <td className="table-cell">
                    <div className="font-medium">{q.client?.name || '—'}</div>
                    <div className="text-xs text-slate-500">{q.client?.phone || ''}</div>
                  </td>
                  <td className="table-cell text-slate-600">{q.items.length}</td>
                  <td className="table-cell text-right font-semibold tabular-nums">{formatCurrency(q.total)}</td>
                  <td className="table-cell">
                    <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_META[q.status]?.color || ''}`}>
                      {STATUS_META[q.status]?.label || q.status}
                    </span>
                  </td>
                  <td className="table-cell text-xs text-slate-500">
                    {new Date(q.created_at).toLocaleDateString('es-MX')}
                  </td>
                  <td className="table-cell text-right">
                    <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <QuoteFormModal open={openCreate} onOpenChange={setOpenCreate} onSaved={refresh} />
      {viewing && <QuoteViewModal quote={viewing} onClose={() => setViewing(null)} onChanged={refresh} />}
    </div>
  );
}

function QuoteFormModal({ open, onOpenChange, onSaved }) {
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [clientId, setClientId] = useState('');
  const [items, setItems] = useState([{ description: '', price: '', service_id: null }]);
  const [notes, setNotes] = useState('');
  const [validity, setValidity] = useState(15);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClientId(''); setItems([{ description: '', price: '', service_id: null }]); setNotes(''); setValidity(15);
    Promise.all([getDetailingClients(), getDetailingServices()])
      .then(([c, s]) => { setClients(c.data || []); setCatalog(s.data || []); })
      .catch(() => {});
  }, [open]);

  const updateItem = (i, field, value) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };

  const pickFromCatalog = (i, serviceId) => {
    const s = catalog.find(x => x.id === serviceId);
    if (!s) return;
    updateItem(i, 'description', s.name);
    updateItem(i, 'service_id', s.id);
    if (s.suggested_price != null && !items[i].price) updateItem(i, 'price', String(s.suggested_price));
  };

  const total = items.reduce((sum, it) => sum + (parseFloat(it.price) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error('Selecciona un cliente'); return; }
    const cleanItems = items
      .filter(i => (i.description || '').trim() && parseFloat(i.price) > 0)
      .map(i => ({ description: i.description.trim(), price: parseFloat(i.price), service_id: i.service_id || null }));
    if (cleanItems.length === 0) { toast.error('Agrega al menos un ítem con precio'); return; }

    setBusy(true);
    try {
      await createDetailingQuote({ client_id: clientId, items: cleanItems, notes: notes || null, validity_days: validity });
      toast.success('Cotización creada');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-quote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-violet-600" /> Nueva cotización</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="quote-client-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && <div className="p-3 text-sm text-slate-400">Crea primero un cliente en la pestaña &quot;Clientes&quot;</div>}
                  {clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Validez (días)</Label>
              <Input type="number" value={validity} onChange={(e) => setValidity(parseInt(e.target.value, 10) || 15)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Servicios / ítems</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { description: '', price: '', service_id: null }])} data-testid="add-quote-item-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Agregar línea
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start p-2 bg-slate-50 rounded-lg" data-testid={`quote-item-${i}`}>
                  <div className="col-span-7">
                    <Input
                      list="catalog-list"
                      value={it.description}
                      onChange={(e) => {
                        updateItem(i, 'description', e.target.value);
                        const match = catalog.find(c => c.name.toLowerCase() === e.target.value.toLowerCase());
                        updateItem(i, 'service_id', match ? match.id : null);
                        if (match && match.suggested_price != null && !it.price) updateItem(i, 'price', String(match.suggested_price));
                      }}
                      placeholder="Concepto (libre o del catálogo)"
                      data-testid={`quote-item-desc-${i}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Input type="number" step="0.01" value={it.price} onChange={(e) => updateItem(i, 'price', e.target.value)} placeholder="Precio MXN" data-testid={`quote-item-price-${i}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <datalist id="catalog-list">
              {catalog.map(c => <option key={c.id} value={c.name}>{c.suggested_price != null ? `$${c.suggested_price}` : ''}</option>)}
            </datalist>
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Términos, observaciones, condiciones..." />
          </div>

          <div className="flex items-center justify-between text-lg font-bold p-3 bg-violet-50 rounded-lg">
            <span>Total</span>
            <span className="text-violet-700 tabular-nums">{formatCurrency(total)}</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="save-quote-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear cotización'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuoteViewModal({ quote, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);

  const shareUrl = quote.share_token ? `${window.location.origin}/cotizacion/${quote.share_token}` : null;
  const CATALOG_URL = 'https://drive.google.com/file/d/1iMGIYAQG1GuqMP3VYdij0AbJxfgzCLU_/view?usp=drivesdk';

  const handleStatus = async (newStatus) => {
    setBusy(true);
    try {
      await updateDetailingQuoteStatus(quote.id, newStatus);
      toast.success(`Estado: ${STATUS_META[newStatus]?.label}`);
      onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    setBusy(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar cotización?')) return;
    setBusy(true);
    try {
      await deleteDetailingQuote(quote.id);
      toast.success('Eliminada');
      onChanged(); onClose();
    } catch { toast.error('Error'); }
    setBusy(false);
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Enlace copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar. Selecciona el enlace manualmente.');
    }
  };

  const handleWhatsApp = () => {
    const lines = [
      `*Cotización ${quote.folio}* — WD Wrap & Detailing Studio`,
      `Cliente: ${quote.client?.name || ''}`,
      quote.client?.vehicle_info ? `Vehículo: ${quote.client.vehicle_info}` : null,
      '',
      ...quote.items.map(i => `• ${i.description} — $${i.price.toFixed(2)}`),
      '',
      `*Total: $${quote.total.toFixed(2)} MXN*`,
      quote.notes ? `\nNotas: ${quote.notes}` : null,
      `\nValidez: ${quote.validity_days} días`,
      shareUrl ? `\n👉 Aprueba en línea:\n${shareUrl}` : null,
      `\n📖 Catálogo completo:\n${CATALOG_URL}`,
    ].filter(Boolean).join('\n');
    const phone = (quote.client?.phone || '').replace(/\D/g, '');
    const url = `https://wa.me/${phone ? '52' + phone : ''}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank');
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-view-modal">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <img src="/wd-detailing-logo.jpg" alt="WD" className="w-14 h-14 object-contain rounded-lg bg-slate-900" data-testid="quote-logo" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <DialogTitle className="flex items-center gap-2 font-mono text-violet-700">{quote.folio}</DialogTitle>
                <span className={`text-xs px-3 py-1 rounded-full border ${STATUS_META[quote.status]?.color || ''}`}>
                  {STATUS_META[quote.status]?.label || quote.status}
                </span>
              </div>
              <DialogDescription className="mt-1">
                WD Wrap &amp; Detailing Studio · {new Date(quote.created_at).toLocaleString('es-MX')} · {quote.created_by_name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div className="font-semibold">{quote.client?.name}</div>
            {quote.client?.phone && <div className="text-slate-600">{quote.client.phone}</div>}
            {quote.client?.vehicle_info && <div className="text-slate-500 text-xs mt-1">{quote.client.vehicle_info}</div>}
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr><th className="text-left p-2">Concepto</th><th className="text-right p-2">Precio</th></tr>
              </thead>
              <tbody>
                {quote.items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="p-2">{it.description}</td>
                    <td className="p-2 text-right font-mono">{formatCurrency(it.price)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-300 bg-violet-50">
                  <td className="p-2 font-bold">TOTAL</td>
                  <td className="p-2 text-right font-bold text-violet-700">{formatCurrency(quote.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {quote.notes && <p className="text-sm text-slate-600 italic">{quote.notes}</p>}
          <p className="text-xs text-slate-400">Validez: {quote.validity_days} días</p>

          {quote.status === 'aceptada' && quote.approved_by_client && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
              ✓ Aprobada por el cliente: <b>{quote.approved_by_client}</b>
              {quote.approved_at && <span className="text-xs text-emerald-600 block">{new Date(quote.approved_at).toLocaleString('es-MX')}</span>}
            </div>
          )}
          {quote.status === 'rechazada' && quote.rejected_reason && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              Rechazada. Motivo: <i>{quote.rejected_reason}</i>
            </div>
          )}

          {/* Public sharing link */}
          {shareUrl && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="share-link-box">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                <Link2 className="w-4 h-4" /> Enlace público para el cliente
              </div>
              <div className="flex items-center gap-2">
                <input readOnly value={shareUrl} className="flex-1 bg-white border border-blue-200 rounded px-2 py-1.5 text-xs font-mono text-slate-700 truncate" data-testid="share-link-input" />
                <Button size="sm" variant="outline" onClick={handleCopyLink} className="gap-1 border-blue-300 text-blue-700" data-testid="copy-share-link-btn">
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </Button>
              </div>
              <p className="text-xs text-blue-700">Al abrirlo, tu cliente puede aprobar o rechazar la cotización en línea.</p>
            </div>
          )}

          {/* Status flow */}
          <div className="flex flex-wrap gap-2">
            {quote.status === 'borrador' && (
              <Button size="sm" onClick={() => handleStatus('enviada')} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="set-status-enviada"><Send className="w-3.5 h-3.5 mr-1" />Marcar enviada</Button>
            )}
            {quote.status === 'enviada' && (
              <>
                <Button size="sm" onClick={() => handleStatus('aceptada')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="set-status-aceptada">Aceptada</Button>
                <Button size="sm" onClick={() => handleStatus('rechazada')} disabled={busy} variant="outline" className="text-red-600 border-red-200" data-testid="set-status-rechazada">Rechazada</Button>
              </>
            )}
            {quote.status === 'aceptada' && (
              <Button size="sm" onClick={() => handleStatus('convertida')} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="set-status-convertida">Marcar convertida</Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={handleWhatsApp} className="gap-2 text-emerald-700" data-testid="quote-whatsapp-btn"><MessageCircle className="w-4 h-4" />WhatsApp</Button>
            <a
              href={CATALOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-sm text-slate-700"
              data-testid="quote-catalog-btn"
            >
              <FileText className="w-4 h-4" /> Ver catálogo <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
            <Button type="button" variant="outline" onClick={() => window.print()} className="gap-2" data-testid="quote-print-btn"><Printer className="w-4 h-4" />Imprimir</Button>
            <Button type="button" variant="outline" onClick={handleDelete} disabled={busy} className="gap-2 text-red-600 ml-auto" data-testid="quote-delete-btn"><Trash2 className="w-4 h-4" />Eliminar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
